<template>
  <div id="app" class="app-root">
    <TelegramWidget />
    <CallbackModal />
    <LeadStickyBar v-if="showLeadBar" />

    <nav v-if="!isHome && !isAdminRoute" class="app-nav">
      <div class="app-nav-inner">
        <router-link to="/" class="app-logo">Mobi<span>FIX</span> PRO</router-link>

        <div class="app-nav-links">
          <router-link to="/" class="app-link">{{ navT.home }}</router-link>
          <router-link to="/services" class="app-link">{{ navT.services }}</router-link>
          <router-link to="/blog" class="app-link">{{ navT.blog }}</router-link>
          <router-link to="/services/iphone-repair" class="app-link">iPhone</router-link>
          <router-link to="/services/samsung-repair" class="app-link">Samsung</router-link>
        </div>

        <div class="app-nav-auth">
          <template v-if="isAuthenticated">
            <router-link to="/orders" class="app-link">{{ navT.orders }}</router-link>
            <router-link to="/profile" class="app-link">{{ navT.profile }}</router-link>
            <button @click="logout" class="app-btn-logout">{{ navT.logout }}</button>
          </template>
          <template v-else>
            <router-link to="/login" class="app-btn-outline">{{ navT.login }}</router-link>
          </template>

          <router-link to="/orders/create" class="app-btn-primary">Замовити ремонт</router-link>

          <button class="app-lang-toggle" @click="locale.setLang(locale.lang === 'uk' ? 'en' : 'uk')">
            {{ locale.lang === 'uk' ? 'EN' : 'UA' }}
          </button>
        </div>
      </div>
    </nav>

    <main class="app-main" :class="{ 'no-nav': isHome || isAdminRoute }">
      <router-view />
      <HomeSeoSection v-if="isHome" />
    </main>

    <footer v-if="!isHome && !isAdminRoute" class="app-footer">
      <div class="app-footer-inner">
        <div class="app-footer-brand">
          <div class="app-footer-logo">Mobi<span>FIX</span> PRO</div>
          <p>Професійний ремонт телефонів у Харкові з акцентом на швидкий фідбек, чесний кошторис, гарантію та зручний запис через сайт і месенджери.</p>
        </div>

        <div class="app-footer-col">
          <div class="app-footer-title">Послуги</div>
          <router-link to="/services/iphone-repair" class="app-footer-link">Ремонт iPhone</router-link>
          <router-link to="/services/samsung-repair" class="app-footer-link">Ремонт Samsung</router-link>
          <router-link to="/services/xiaomi-repair" class="app-footer-link">Ремонт Xiaomi</router-link>
          <router-link to="/services/screen-replacement" class="app-footer-link">Заміна екрана</router-link>
          <router-link to="/services/battery-replacement" class="app-footer-link">Заміна батареї</router-link>
        </div>

        <div class="app-footer-col">
          <div class="app-footer-title">Блог</div>
          <router-link to="/blog" class="app-footer-link">Усі статті</router-link>
          <router-link to="/blog/shcho-robyty-yakshcho-iphone-vpav-u-vodu" class="app-footer-link">iPhone впав у воду</router-link>
          <router-link to="/blog/skilky-koshtuie-zamina-skla-iphone-14" class="app-footer-link">Заміна скла iPhone 14</router-link>
          <router-link to="/blog/yak-vybraty-akumuliator-dlia-samsung" class="app-footer-link">Акумулятор для Samsung</router-link>
        </div>

        <div class="app-footer-col">
          <div class="app-footer-title">Контакти</div>
          <a href="tel:+380682772822" class="app-footer-link">+38 (068) 277-28-22</a>
          <a href="mailto:info@mobifixpro.net" class="app-footer-link">info@mobifixpro.net</a>
          <a href="https://t.me/MobiFIXPRO_bot" target="_blank" rel="noopener" class="app-footer-link">Telegram</a>
          <a href="https://maps.google.com/?q=49.9974233,36.3409755" target="_blank" rel="noopener" class="app-footer-link">Харків, просп. Ювілейний, 57а</a>
        </div>
      </div>

      <div class="app-footer-bottom">
        <span>© 2026 MobiFIX PRO, Харків</span>
        <span>Безкоштовна діагностика · Пн-Сб 9:00-19:00 · Телефон, Telegram, Viber, WhatsApp</span>
      </div>
    </footer>
  </div>
</template>

<script>
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';
import { useLocaleStore } from './stores/locale';
import { applySeo, localBusinessJsonLd } from './utils/seo';
import TelegramWidget from './components/TelegramWidget.vue';
import CallbackModal from './components/CallbackModal.vue';
import LeadStickyBar from './components/LeadStickyBar.vue';
import HomeSeoSection from './components/HomeSeoSection.vue';

export default {
  name: 'App',
  components: {
    TelegramWidget,
    CallbackModal,
    LeadStickyBar,
    HomeSeoSection
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();
    const locale = useLocaleStore();

    const isHome = computed(() => route.name === 'Home');
    const isAdminRoute = computed(() => String(route.path).startsWith('/admin'));
    const isAuthenticated = computed(() => !!authStore.token);
    const showLeadBar = computed(() => !isAdminRoute.value);

    const navT = computed(() => ({
      home: locale.lang === 'uk' ? 'Головна' : 'Home',
      services: locale.lang === 'uk' ? 'Послуги' : 'Services',
      blog: locale.lang === 'uk' ? 'Блог' : 'Blog',
      orders: locale.lang === 'uk' ? 'Мої замовлення' : 'My Orders',
      profile: locale.lang === 'uk' ? 'Профіль' : 'Profile',
      login: locale.lang === 'uk' ? 'Увійти' : 'Log In',
      logout: locale.lang === 'uk' ? 'Вийти' : 'Log Out'
    }));

    const logout = () => {
      authStore.logout();
      router.push('/');
    };

    watch(
      () => route.fullPath,
      () => {
        const seo = route.meta?.seo;
        if (!seo) return;

        applySeo({
          title: seo.title,
          description: seo.description,
          keywords: seo.keywords,
          path: seo.path || route.path,
          robots: seo.robots,
          jsonLd: [localBusinessJsonLd(), ...(seo.jsonLd || [])]
        });
      },
      { immediate: true }
    );

    return {
      locale,
      navT,
      isHome,
      isAdminRoute,
      isAuthenticated,
      showLeadBar,
      logout
    };
  }
};
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }
html,
body {
  max-width: 100%;
  overflow-x: hidden;
}

body { font-family: 'Segoe UI', system-ui, sans-serif; background: #f4f3ff; color: #26215c; }
img { max-width: 100%; }
#app { display: flex; flex-direction: column; min-height: 100vh; }
.app-root { min-height: 100vh; }

.app-nav {
  position: sticky;
  top: 0;
  z-index: 200;
  background: rgba(255, 255, 255, 0.94);
  border-bottom: 0.5px solid #e0dff8;
  backdrop-filter: blur(14px);
  box-shadow: 0 2px 16px rgba(83, 74, 183, 0.06);
}

.app-nav-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0.85rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.2rem;
}

.app-logo {
  font-size: 18px;
  font-weight: 700;
  color: #534ab7;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
}

.app-logo span { color: #1d9e75; }

.app-nav-links {
  display: flex;
  gap: 1.1rem;
  flex: 1;
  min-width: 0;
}

.app-link {
  color: #666;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.15s;
  font-weight: 600;
  white-space: nowrap;
}

.app-link:hover,
.app-link.router-link-active { color: #534ab7; }

.app-nav-auth {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
}

.app-btn-primary,
.app-btn-outline,
.app-btn-logout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  text-decoration: none;
  min-height: 42px;
}

.app-btn-primary {
  background: #534ab7;
  color: #fff;
  padding: 10px 18px;
}

.app-btn-outline {
  background: transparent;
  color: #534ab7;
  border: 1.5px solid #534ab7;
  padding: 8px 16px;
}

.app-btn-logout {
  background: transparent;
  color: #e05252;
  border: 1.5px solid #e05252;
  padding: 8px 16px;
  cursor: pointer;
}

.app-lang-toggle {
  background: #f4f3ff;
  color: #534ab7;
  border: 1.5px solid #e0dff8;
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
  min-height: 42px;
}

.app-main {
  flex: 1;
  max-width: 1180px;
  margin: 0 auto;
  width: 100%;
  padding: 2.5rem 2rem 7rem;
}

.app-main > * {
  min-width: 0;
}

.app-main.no-nav {
  max-width: 100%;
  padding: 0 0 7rem;
}

.app-footer {
  background: #18172d;
  color: #ccd2e2;
}

.app-footer-inner {
  max-width: 1180px;
  margin: 0 auto;
  padding: 3rem 2rem 2rem;
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 2rem;
}

.app-footer-brand p {
  margin-top: 0.8rem;
  font-size: 14px;
  line-height: 1.7;
  color: #a9b0c4;
}

.app-footer-logo {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
}

.app-footer-logo span { color: #1d9e75; }

.app-footer-col { display: flex; flex-direction: column; gap: 10px; }
.app-footer-title { color: #fff; font-size: 12px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; }
.app-footer-link { color: #a9b0c4; text-decoration: none; font-size: 14px; }
.app-footer-link:hover { color: #fff; }

.app-footer-bottom {
  max-width: 1180px;
  margin: 0 auto;
  padding: 1.2rem 2rem 2rem;
  border-top: 1px solid #2b2948;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: #8690aa;
}

@media (max-width: 980px) {
  .app-nav-inner {
    flex-wrap: wrap;
  }

  .app-nav-links {
    order: 3;
    width: 100%;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .app-nav-links::-webkit-scrollbar {
    display: none;
  }

  .app-footer-inner {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 700px) {
  .app-nav-inner,
  .app-footer-inner,
  .app-footer-bottom { padding-left: 1rem; padding-right: 1rem; }

  .app-nav-auth {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
    justify-content: stretch;
  }

  .app-btn-primary {
    grid-column: 1 / -1;
    width: 100%;
  }

  .app-nav-auth > .app-link,
  .app-btn-outline,
  .app-btn-logout,
  .app-lang-toggle {
    width: 100%;
    min-width: 0;
    min-height: 42px;
    padding: 8px 10px;
    border-radius: 10px;
    text-align: center;
    justify-content: center;
  }

  .app-nav-auth > .app-link {
    display: inline-flex;
    align-items: center;
    border: 1.5px solid #e0dff8;
    background: #fff;
  }

  .app-nav-links {
    gap: 0.55rem;
  }

  .app-link {
    font-size: 13px;
  }

  .app-main { padding: 1rem 1rem 7rem; }
  .app-main.no-nav { padding-bottom: 7rem; }
  .app-footer-inner { grid-template-columns: 1fr; }
  .app-footer-bottom { flex-direction: column; }
}
</style>
